import { forwardRef } from "react";
import { cn } from "@/lib/cn";

/**
 * Controles de formulario homologados (doc UX 20260627: "homologar la altura de
 * todos los controles; mejorar la apariencia de los campos deshabilitados;
 * revisar el espaciado vertical"). Altura fija `h-10` para alinear con
 * `Button size="md"`.
 */

const CONTROL_BASE =
  "w-full rounded-md border border-edge-strong bg-surface px-3 text-sm text-fg shadow-sm transition " +
  "placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 " +
  "disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle";

/** Envuelve un control con label, marca de requerido, ayuda y error. */
export function FormField({
  label,
  htmlFor,
  required,
  hint,
  error,
  className,
  children,
}: {
  label?: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label htmlFor={htmlFor} className="block text-sm font-medium text-fg-muted">
          {label}
          {required && <span className="ml-0.5 text-estado-err">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-xs text-estado-err">{error}</p>
      ) : (
        hint && <p className="text-xs text-fg-subtle">{hint}</p>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(CONTROL_BASE, "h-10", className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cn(CONTROL_BASE, "h-10 pr-8", className)} {...rest}>
        {children}
      </select>
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 4, ...rest }, ref) {
    return <textarea ref={ref} rows={rows} className={cn(CONTROL_BASE, "py-2", className)} {...rest} />;
  },
);

/**
 * Par etiqueta/valor de solo lectura (antes el helper `Field` duplicado en cada
 * página de detalle/resumen). Mantiene la jerarquía: etiqueta tenue arriba,
 * valor con peso normal abajo.
 */
export function DataField({
  label,
  value,
  className,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs font-medium uppercase tracking-wide text-fg-subtle">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg">{value ?? "—"}</dd>
    </div>
  );
}
